
import React, { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { getErrorMessage } from 'utils'

import { useSnackbar } from 'contexts'
import { useCompanyQuery, useCompanyMutationsQuery } from 'hooks/api'
import { useSetCompanySourceChannel, useSourceChannels } from 'hooks/api/useGtmSales'
import { useCanAccess } from 'hooks'
import { NO_SOURCE_CHANNEL, NOT_RECORDED_LABEL, SOURCE_CHANNEL_PICKER_OPTIONS } from 'constants/sourceChannels'
import type { SourceChannel } from '@repo/types'
import { useForm, Controller } from 'react-hook-form'
import { ROUTES } from 'routes'
import { CompanyEditPageSkeleton } from './CompanyEditPageSkeleton'
import { COMPANY_RULES } from 'validation/company'

import { Box, Stack, Typography } from '@mui/material'

import { Button } from '@repo/ui'
import { SelectCountryField, Field, StickyFormHeader, FormSubmitButton, SelectField } from 'components'

import { UploadLogo } from './components'


interface CompanyEditPageProps {
  type: 'create' | 'edit'
}

export const CompanyEditPage = (props: CompanyEditPageProps) => {
  const { type } = props

  const { id } = useParams()
  const navigate = useNavigate()
  const methods = useForm()
  const { open: notification } = useSnackbar()

  const { control, setValue, handleSubmit, reset, getValues } = methods

  const isCreate = type === 'create'
  const isEdit = type === 'edit'

  const { data, isLoading } = useCompanyQuery({ id: id as string }, { enabled: isEdit })

  // VRG-675 — the company's source channel lives in its own internal table (never in a
  // company payload), so it loads and saves beside the company rather than inside it.
  // Writes are platform_admin only on the server; others see the field disabled.
  const { isHasAccess: isPlatformAdmin } = useCanAccess({ role: 'platform_admin' })
  const channels = useSourceChannels()
  const setChannel = useSetCompanySourceChannel()
  const storedChannel: SourceChannel | null = isEdit && id ? channels.data?.companies[id] ?? null : null

  const { create, update, isLoading: isMutationLoading } = useCompanyMutationsQuery({
    onSuccess: async data => {
      const wanted = (getValues('source_channel') ?? NO_SOURCE_CHANNEL) as string
      if (wanted !== (storedChannel ?? NO_SOURCE_CHANNEL) && data?.id) {
        try {
          await setChannel.mutateAsync({ companyId: data.id, sourceChannel: wanted ? (wanted as SourceChannel) : null })
        } catch (e) {
          notification({ type: 'error', title: `Company saved, but its source channel was not: ${getErrorMessage(e)}` })
        }
      }
      notification({
        type: 'success',
        title: `Company: ${data.name} ${isCreate ? 'created' : 'updated'}`,
      })
      navigate('/' + ROUTES.INTERNAL_COMPANIES)

    },

    onError: error => {
      const message = getErrorMessage(error)

      notification({
        type: 'error',
        title: message,
      })
    },
  })

  // `reset`, not setValue — same defect as ProductEditPage carried here.
  // `useForm()` is called with no defaultValues, so the form's baseline is empty;
  // setValue changes the values without moving that baseline, leaving react-hook-form
  // comparing a filled form against nothing and reporting isDirty === true on load.
  // That makes the Save-until-dirty gate (working-conventions §52) dead code — the
  // button is live before the user types. `reset` makes the loaded record the
  // baseline, so the form is pristine until actually edited.
  //
  // Guarded on `data` as well as isEdit: without it a create-mode render, or an edit
  // render before the fetch resolves, would reset the form to a row of undefineds and
  // wipe anything already typed.
  const channelsReady = !channels.isLoading
  useEffect(() => {
    if (isEdit && data && channelsReady) {
      reset({
        name: data.name,
        logo: [
          {
            file: null,
            preview_url: data.logo,
            isUploaded: true,
          },
        ],
        country: data.country,
        source_channel: storedChannel ?? NO_SOURCE_CHANNEL,
      })
    }
  }, [isEdit, data, reset, channelsReady, storedChannel])

  const onSubmit = handleSubmit(async data => {
    if (isCreate) {
      create({
        provider: 'shopify',
        name: data.name,
        logo: data.logo[0],
        country: data.country.code,
        priceId: data.priceId,
        email: data.email,
      })
    }

    if (isEdit) {
      update({
        id: id as string,
        name: data.name,
        logo: data.logo[0],
        country: data.country.code,
      })
    }
  })

  const onCancel = () => {
    navigate('/' + ROUTES.INTERNAL_COMPANIES)
  }

  return (
    <>
      {isLoading || (isEdit && !channelsReady) ? (
        // Footprint-parity placeholder (§19) in place of the centred <Loader />, which reserved none
        // of the 94px sticky header or the 448px field stack. See CompanyEditPageSkeleton.
        <CompanyEditPageSkeleton />
      ) : (
        <Box px="24px" pb="24px">
          {/* No `pt` on the Box above: it sits INSIDE the Layout's own 24px scrollport padding,
              so a second 24px pushed this form's title to 76 when the page-header datum is 44.
              The horizontal inset stays — that is the form column's inset, not a top offset.

              Now on the shared Header (via StickyFormHeader), so ReferrerBackLink mounts
              itself — the explicit one this page carried was only needed because it
              hand-rolled its title. §42 */}
          <StickyFormHeader
            title={`${isCreate ? 'Create' : 'Edit'} company`}
            actions={
              <>
                {/* Cancel stays enabled always — it's the page's exit affordance,
                    not a discard action, so a pristine form must not strand the
                    user with no way out but the breadcrumb. */}
                <Button variant="secondary" onClick={() => onCancel()} disabled={isMutationLoading}>
                  Cancel
                </Button>
                {/* Disabled until dirty + disabled on a field error (VRG-364).
                    `requireDirty={isEdit}`: a create form starts empty and has
                    nothing to be dirty against, mirroring ProductEditPage's
                    `page === 'edit'` split. */}
                <FormSubmitButton
                  control={control}
                  requireDirty={isEdit}
                  onClick={() => onSubmit()}
                  disabled={isMutationLoading}
                >
                  {isCreate ? 'Create' : 'Save'}
                </FormSubmitButton>
              </>
            }
          />

          <Stack spacing="12px" mt="16px">
            {/* <BlockWithBorder title="Company logo"> */}
              <Controller
                control={control}
                name="logo"
                rules={COMPANY_RULES.logo}
                render={({ field: { onChange, value }, fieldState: { invalid, error } }) => {
                  return (
                    <>
                      <UploadLogo file={value} onChange={onChange} />

                      {invalid && (
                        <Typography variant="body4" color="error" mt="4px">
                          {error?.message}
                        </Typography>
                      )}
                    </>
                  )
                }}
              />
            {/* </BlockWithBorder> */}

            {/* <BlockWithBorder title="Basic info"> */}
              <Stack spacing="24px" width={300}>
                <Field name="name" label="Company Name" {...methods} rules={COMPANY_RULES.name} />
                <Field name="email" label="Email" {...methods} rules={COMPANY_RULES.email} />
                <SelectCountryField
                  label="Country"
                  name="country"
                  control={control}
                  disabled={isEdit}
                />
                <Controller
                  control={control}
                  name="priceId"
                  defaultValue={'price_1PVHLODV6OVY2V7LyjHbtSgi'}
                  rules={COMPANY_RULES.priceId}
                  render={({ field: { onChange, value }, fieldState: { invalid, error } }) => {
                    return <Field name="priceId" label="Price ID" {...methods} rules={COMPANY_RULES.priceId} />
                  }}
                />
                {/* VRG-675 — which Traction channel brought this company (GTM → Sales). */}
                <Controller
                  control={control}
                  name="source_channel"
                  defaultValue={NO_SOURCE_CHANNEL}
                  render={({ field: { onChange, value } }) => (
                    <SelectField
                      label="Source channel"
                      options={SOURCE_CHANNEL_PICKER_OPTIONS}
                      placeholder={NOT_RECORDED_LABEL}
                      value={value ?? NO_SOURCE_CHANNEL}
                      search
                      disabled={!isPlatformAdmin}
                      onChange={(v) => onChange(String(v))}
                    />
                  )}
                />
              </Stack>
            {/* </BlockWithBorder> */}
          </Stack>

        </Box>
      )}
    </>
  )
}
